import React, { useState } from 'react';
import './projects.css';

export default function Projects() {
  const [expandedProject, setExpandedProject] = useState(null);

  const toggleExpand = (id) => {
    if (expandedProject === id) {
      setExpandedProject(null);
    } else {
      setExpandedProject(id);
    }
  };

  const primaryProjects = [
    {
      id: 'craftiq',
      title: 'CraftIQ – AI SaaS Platform',
      category: 'AI SaaS Product',
      description: 'A collaborative, multi-model creative platform designed to align brand parameters, parse media documents, and generate on-brand assets in real time.',
      metrics: [
        { value: '60%', label: 'UX Speedup' },
        { value: 'Async', label: 'FastAPI Queue' },
        { value: 'SSE', label: 'Streaming' }
      ],
      visual: (
        <svg viewBox="0 0 100 100" width="100%" height="100%">
          <rect x="15" y="15" width="70" height="70" rx="10" fill="none" stroke="rgba(255,255,255,0.05)" />
          <rect x="25" y="25" width="50" height="30" rx="4" fill="#111" stroke="#4F46E5" strokeWidth="1" />
          <circle cx="35" cy="70" r="5" fill="#10b981" />
          <circle cx="50" cy="70" r="5" fill="rgba(255,255,255,0.2)" />
          <circle cx="65" cy="70" r="5" fill="rgba(255,255,255,0.2)" />
        </svg>
      ),
      details: {
        problem: 'Marketing teams struggle to verify visual assets and text copy against brand guidelines, leading to manual review bottlenecks and misaligned campaigns.',
        solution: 'Developed a SaaS hub allowing teams to upload corporate style guides, index guidelines into semantic search stores, and generate copy/imagery.',
        challenges: 'Inference calls to visual models took up to 30 seconds, leading to connection timeouts and negative client experiences.',
        engineering: 'Resolved latency gaps by implementing an asynchronous task queue using FastAPI background workers and streaming generation logs via Server-Sent Events (SSE).',
        tradeoffs: [
          { decision: 'FastAPI vs Node.js', choice: 'FastAPI', reasoning: 'Enabled clean, native interfaces for Python ML pipelines and superior asynchronous coroutines performance.' },
          { decision: 'Supabase vs Local Postgres', choice: 'Supabase', reasoning: 'Supplied out-of-the-box storage buckets for user media files and secure user auth logic, reducing engineering overhead.' }
        ],
        github: 'https://github.com/rishabh-108272',
        demo: 'https://github.com/rishabh-108272'
      }
    },
    {
      id: 'biomarker-rag',
      title: 'Multi-Agentic RAG for Biomarkers',
      category: 'Agentic AI / Biotech',
      description: 'An autonomous, stateful multi-agent system designed to search, extract, and validate bio-clinical biomarker assertions from literature repositories.',
      metrics: [
        { value: '<8%', label: 'Hallucinations' },
        { value: '15ms', label: 'Index Query' },
        { value: 'LangGraph', label: 'State Routing' }
      ],
      visual: (
        <svg viewBox="0 0 100 100" width="100%" height="100%">
          <circle cx="50" cy="50" r="25" fill="none" stroke="#4F46E5" strokeWidth="1.5" />
          <circle cx="50" cy="50" r="10" fill="#111" stroke="rgba(255,255,255,0.2)" />
          <path d="M 50 10 L 50 25 M 50 75 L 50 90 M 10 50 L 25 50 M 75 50 L 90 50" stroke="#10b981" strokeWidth="1.5" />
        </svg>
      ),
      details: {
        problem: 'Biochemists manual validation of biomarker targets requires scanning thousands of publications, leading to researcher search bottlenecks.',
        solution: 'Built a search extraction network utilizing state routing to scan clinical registries and cross-validate assertions with research citations.',
        challenges: 'Standard vector retrieval returned irrelevant context chunks, leading to LLM hallucinations on clinical attributes.',
        engineering: 'Configured a semantic reranker cross-encoder step on top of FAISS vector query returns and structured output verification gates checking citation overlaps.',
        tradeoffs: [
          { decision: 'FAISS vs ChromaDB', choice: 'FAISS', reasoning: 'Lower latency for in-memory indexing of structured clinical subset papers.' },
          { decision: 'LangGraph vs Custom loops', choice: 'LangGraph', reasoning: 'Allowed structured state tracking and node transitions, simplifying recursive error-correction routines.' }
        ],
        github: 'https://github.com/rishabh-108272/Biomarker_Classification_And_Prediction',
        demo: 'https://github.com/rishabh-108272/Biomarker_Classification_And_Prediction'
      }
    },
    {
      id: 'omics-dash',
      title: 'Omics AI SaaS Platform',
      category: 'Bioinformatics SaaS',
      description: 'A genomic sequence analysis dashboard enabling medical researchers to upload DNA datasets and run classification models.',
      metrics: [
        { value: 'PyTorch', label: 'Inference' },
        { value: '98%', label: 'Visual Accuracy' },
        { value: 'Docker', label: 'Environment' }
      ],
      visual: (
        <svg viewBox="0 0 100 100" width="100%" height="100%">
          <path d="M 20 20 L 80 20 L 80 80 L 20 80 Z" fill="none" stroke="rgba(255,255,255,0.05)" />
          <path d="M 25 35 Q 50 75 75 35" fill="none" stroke="#4F46E5" strokeWidth="1.5" />
          <path d="M 25 65 Q 50 25 75 65" fill="none" stroke="#10b981" strokeWidth="1.5" />
        </svg>
      ),
      details: {
        problem: 'Bioinformatic sequence modeling demands intense local environments and terminal tools, preventing clinic-level exploration and dashboard reviews.',
        solution: 'Constructed a secure SaaS gateway dashboard deploying PyTorch sequence classification classifiers and rendering biological metrics.',
        challenges: 'Deploying deep learning dependencies caused package conflicts and unstable cloud run times.',
        engineering: 'Isolated model inference runtimes into custom Docker containers, exposing interfaces via optimized FastAPI routers.',
        tradeoffs: [
          { decision: 'Docker vs Bare-Metal', choice: 'Docker', reasoning: 'Enforces complete environment reproducibility across development and production servers.' },
          { decision: 'FastAPI vs Django Web', choice: 'FastAPI', reasoning: 'Asynchronous event loops support high-performance file parsing and rapid response returns.' }
        ],
        github: 'https://github.com/rishabh-108272',
        demo: 'https://github.com/rishabh-108272'
      }
    }
  ];

  const secondaryProjects = [
    {
      title: 'CheckMate Compliance SaaS',
      tech: 'FastAPI • React • Schema Validation',
      desc: 'An automated platform validating software requirements and tech descriptions against custom industry regulatory standards.'
    },
    {
      title: 'Early Diabetic Foot Ulcer Prediction',
      tech: 'TensorFlow • CNNs • Healthcare CV',
      desc: 'A computer vision model classifying clinical images to detect and warn of ulcer formations early, helping prevent complications.'
    },
    {
      title: 'Pathwise AI Career Counselor',
      tech: 'Django • LangChain • PostgreSQL',
      desc: 'A conversational agent integrating chat history memory buffers to recommend customized career roadmaps based on user profiles.'
    }
  ];

  return (
    <div className="projects-container">
      <div className="projects-header">
        <span className="section-tag">Case Studies</span>
        <h2>Technical Showcases</h2>
        <p>
          Click on any featured project card below to expand its architectural layout, engineering decisions, and trade-off comparisons.
        </p>
      </div>

      <div className="projects-grid">
        {primaryProjects.map((project) => {
          const isExpanded = expandedProject === project.id;
          return (
            <div key={project.id} style={{ display: 'flex', flexDirection: 'column' }}>
              <div 
                className="project-card-premium premium-card"
                onClick={() => toggleExpand(project.id)}
              >
                <div className="project-left">
                  <div className="project-meta">
                    <span className="project-category">{project.category}</span>
                  </div>
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-desc">{project.description}</p>
                  
                  <div className="project-metrics-row">
                    {project.metrics.map((m, i) => (
                      <div className="metric-box" key={i}>
                        <span className="metric-val">{m.value}</span>
                        <span className="metric-lbl">{m.label}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ color: 'var(--accent-glow)', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.5rem' }}>
                    {isExpanded ? 'Collapse case study ↑' : 'Expand detailed case study ↓'}
                  </div>
                </div>

                <div className="project-visual-box">
                  {project.visual}
                </div>
              </div>

              {isExpanded && (
                <div className="case-study-expanded">
                  <div className="case-study-grid">
                    <div className="case-study-block">
                      <h4>The Problem</h4>
                      <p>{project.details.problem}</p>

                      <h4>The Solution</h4>
                      <p>{project.details.solution}</p>
                    </div>

                    <div className="case-study-block">
                      <h4>Engineering Challenge</h4>
                      <p>{project.details.challenges}</p>

                      <h4>How I Solved It</h4>
                      <p>{project.details.engineering}</p>
                    </div>
                  </div>

                  <div className="case-study-block" style={{ marginTop: '2rem' }}>
                    <h4>Architecture Trade-Offs</h4>
                    <table className="tradeoffs-table">
                      <thead>
                        <tr>
                          <th>Decision Boundary</th>
                          <th>Selected Tech</th>
                          <th>Engineering Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {project.details.tradeoffs.map((t, idx) => (
                          <tr key={idx}>
                            <td><strong>{t.decision}</strong></td>
                            <td><span className="tech-badge">{t.choice}</span></td>
                            <td>{t.reasoning}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="action-row">
                    <a href={project.details.demo} target="_blank" rel="noopener noreferrer">
                      <button className="talk-btn" style={{ padding: '0.6rem 1.5rem', fontSize: '0.85rem' }}>Live Demo</button>
                    </a>
                    <a href={project.details.github} target="_blank" rel="noopener noreferrer">
                      <button className="resume-btn" style={{ padding: '0.6rem 1.5rem', fontSize: '0.85rem' }}>GitHub Source</button>
                    </a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Secondary Projects Section */}
      <div className="secondary-projects-header">
        <span className="section-tag">Other Implementations</span>
        <h2>Systems & Diagnostic Utilities</h2>
      </div>

      <div className="secondary-grid">
        {secondaryProjects.map((p, index) => (
          <div className="secondary-card premium-card" key={index}>
            <span className="project-category" style={{ fontSize: '0.7rem' }}>{p.tech}</span>
            <h3>{p.title}</h3>
            <p>{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}